import { EStickerVariants } from '@monofront/design-system/visuals/Backgrounds/StickerBackground';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { IPrismicImage, IPrismicRichText } from '@monofront/types';
import { RichText } from 'prismic-reactjs';
import React from 'react';
import Bubble from '@components/Bubble';
import { useMediaQuery } from '@monofront/business/hooks';

interface ISingleBubbleProps {
  image: IPrismicImage;
  title: IPrismicRichText;
  text: IPrismicRichText;
  position?: 'top-left' | 'bottom-right';
  variant?: EStickerVariants;
}

const SingleBubble: React.FC<ISingleBubbleProps> = ({
  image,
  title,
  text,
  position,
  variant,
}) => {
  const positionConditions = position?.split('-') || [];
  const isMobile = useMediaQuery(800);

  const imageHeight = isMobile ? 100 : image?.dimensions?.height;
  const imageWidth = isMobile ? 100 : image?.dimensions?.width;

  return (
    <>
      <section className="tw-flex tw-items-center tw-justify-center">
        <div className="tw-relative">
          <Bubble
            title={<RichText render={title} />}
            text={<RichText render={text} />}
            variant={variant || EStickerVariants.D}
            width={isMobile ? 259 : 282}
            height={isMobile ? 157 : 181}
          />
          {image?.url && (
            <div className="image-container">
              <ResponsiveImage
                src={image.url}
                width={imageWidth}
                height={imageHeight}
                layout="fixed"
                alt={image?.alt || ''}
              />
            </div>
          )}
        </div>
      </section>
      <style jsx>{`
        .image-container {
          position: absolute;
          top: ${positionConditions.includes('top')
            ? `-${imageHeight / 2.5}px`
            : 'auto'};
          bottom: ${positionConditions.includes('bottom')
            ? `-${imageHeight / 2.5}px`
            : 'auto'};
          left: ${positionConditions.includes('left')
            ? `-${imageWidth / 4}px`
            : 'auto'};
          right: ${positionConditions.includes('right')
            ? `-${imageWidth / 4}px`
            : 'auto'};
        }
      `}</style>
    </>
  );
};

interface IBubblesProps {
  bubbles: ISingleBubbleProps[];
  spacer?: React.ReactNode;
}

const Bubbles: React.FC<IBubblesProps> = ({ bubbles, spacer }) => (
  <section className="tw-flex tw-flex-col tw-items-center tw-justify-around tw-gap-x-8 tw-gap-y-7 md:tw-flex-row lg:tw-gap-x-24">
    {bubbles?.map(({ image, title, text, position }, index) => (
      <React.Fragment key={index}>
        <SingleBubble
          image={image}
          title={title}
          text={text}
          position={position}
        />
        {(index < bubbles.length - 1 && spacer) || null}
      </React.Fragment>
    ))}
  </section>
);

export default Bubbles;
